import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MonitorPlay, FileImage, Calendar, Activity } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Dashboard() {
  const [stats, setStats] = useState({
    screens: 0,
    content: 0,
    schedules: 0,
    activeScreens: 0
  });
  const [screens, setScreens] = useState([]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [screensRes, contentRes, schedulesRes] = await Promise.all([
        axios.get(`${API_URL}/screens`),
        axios.get(`${API_URL}/content`),
        axios.get(`${API_URL}/schedules`)
      ]);
      
      setScreens(screensRes.data);
      setStats({
        screens: screensRes.data.length,
        content: contentRes.data.length,
        schedules: schedulesRes.data.length,
        activeScreens: screensRes.data.filter(s => s.status === 'active').length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const statCards = [
    { title: 'Ekrany', value: stats.screens, icon: MonitorPlay, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { title: 'Aktywne', value: stats.activeScreens, icon: Activity, color: 'text-green-400', bg: 'bg-green-500/10' },
    { title: 'Treści', value: stats.content, icon: FileImage, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    { title: 'Harmonogramy', value: stats.schedules, icon: Calendar, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  ];

  return (
    <Layout>
      <div data-testid="dashboard-container" className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Dashboard</h1>
          <p className="text-zinc-400">Prze gląd systemu zarządzania wyświetlaczami AvePark</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title} data-testid={`stat-card-${stat.title.toLowerCase()}`} className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-zinc-400">{stat.title}</CardTitle>
                  <div className={`p-2 rounded-lg ${stat.bg}`}>
                    <Icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stat.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Podgląd ekranów na żywo</CardTitle>
            <CardDescription className="text-zinc-400">Statusy wszystkich ekranów w systemie</CardDescription>
          </CardHeader>
          <CardContent>
            {screens.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">Brak ekranów. Dodaj ekrany w zakładce Ekrany.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {screens.map((screen) => {
                  const displayUrl = screen.type === 'connected' && screen.group_id
                    ? `/display/group/${screen.group_id}`
                    : `/display/${screen.id}`;
                  
                  return (
                    <div
                      key={screen.id}
                      data-testid={`screen-preview-${screen.id}`}
                      className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-blue-500 transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-white">{screen.name}</h3>
                        <span className={`status-badge px-2 py-1 text-xs rounded-full ${
                          screen.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'
                        }`}>
                          {screen.status === 'active' ? 'Aktywny' : 'Nieaktywny'}
                        </span>
                      </div>
                      <div className="text-sm text-zinc-400 space-y-1 mb-3">
                        <p>Pozycja: {screen.position}</p>
                        <p>Typ: {screen.type === 'connected' ? 'Połączony' : 'Samodzielny'}</p>
                        <p>Rozdzielczość: {screen.resolution}</p>
                        {screen.group_id && <p>Grupa: {screen.group_id}</p>}
                      </div>
                      <a
                        href={displayUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center px-3 py-2 text-sm bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg border border-blue-500/30 hover:border-blue-500/50 transition-all"
                      >
                        🖥️ Otwórz podgląd
                      </a>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}